import React, { useMemo, useState } from "react";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { Input, Select } from "antd";
import { PRIM_TABS } from "./spr-number-price-utils";
import { PrimKindIcon, primKindUi } from "./spr-number-price-prim-ui";

const MiniSelect = ({ autoFocus, defaultOpen, disabled, options, placeholder, value, onChange, width }) => (
  <Select
    autoFocus={autoFocus}
    defaultOpen={defaultOpen}
    disabled={disabled}
    showSearch
    optionFilterProp="label"
    placeholder={placeholder}
    value={value}
    options={options}
    popupMatchSelectWidth={false}
    variant="borderless"
    suffixIcon={null}
    style={{ width }}
    onChange={onChange}
  />
);

const labelOf = (options, raw) => {
  const found = (options || []).find((o) => String(o.value) === String(raw));
  return found?.label ?? String(raw);
};

/**
 * Фильтр строк прайса: наименование + чипы применимости через «+».
 */
const SprNumberPriceUslFilter = ({ name, onNameChange, primFilters, onPrimFiltersChange, optionsByVid }) => {
  const [adding, setAdding] = useState(false);
  const [draftVid, setDraftVid] = useState(null);
  const [editVid, setEditVid] = useState(null);

  const chips = useMemo(
    () =>
      PRIM_TABS.filter((tab) => primFilters?.[tab.vid] != null).map((tab) => ({
        vid: tab.vid,
        kindLabel: tab.shortLabel || tab.label,
        valueLabel: labelOf(optionsByVid?.[tab.vid], primFilters[tab.vid])
      })),
    [primFilters, optionsByVid]
  );

  const used = new Set(chips.map((c) => c.vid));
  const availableKinds = PRIM_TABS.filter((tab) => !used.has(tab.vid)).map((tab) => ({
    value: tab.vid,
    label: tab.shortLabel || tab.label
  }));
  const canAdd = availableKinds.length > 0;

  const setVidValue = (vid, next) => {
    onPrimFiltersChange({
      ...primFilters,
      [vid]: next
    });
  };

  const clearVid = (vid) => {
    const next = { ...primFilters };
    delete next[vid];
    onPrimFiltersChange(next);
    if (editVid === vid) setEditVid(null);
  };

  const startAdd = () => {
    setEditVid(null);
    setDraftVid(null);
    setAdding(true);
  };

  const cancelAdd = () => {
    setAdding(false);
    setDraftVid(null);
  };

  const pickDraftValue = (next) => {
    if (draftVid == null) return;
    setVidValue(draftVid, next);
    cancelAdd();
  };

  return (
    <div className="spr-number-price-usl-filter" role="group" aria-label="Фильтр строк прайса">
      <Input
        allowClear
        size="small"
        variant="borderless"
        className="spr-number-price-usl-filter__name"
        placeholder="Фильтр по наименованию"
        value={name}
        onChange={(e) => onNameChange(e.target.value)}
      />
      {chips.map((chip) =>
        editVid === chip.vid ? (
          <span key={chip.vid} className="spr-number-price-usl-filter__draft">
            <span
              className="spr-number-price-usl-filter__chip-kind"
              style={{ color: primKindUi(chip.vid).color }}
              title={chip.kindLabel}
            >
              <PrimKindIcon vid={chip.vid} />
            </span>
            <MiniSelect
              autoFocus
              defaultOpen
              options={optionsByVid?.[chip.vid] || []}
              placeholder="Значение"
              value={primFilters[chip.vid]}
              width={140}
              onChange={(next) => {
                setVidValue(chip.vid, next);
                setEditVid(null);
              }}
            />
            <button
              type="button"
              className="spr-number-price-usl-filter__clear"
              title="Закрыть"
              aria-label="Закрыть"
              onClick={() => setEditVid(null)}
            >
              <CloseOutlined />
            </button>
          </span>
        ) : (
          <span key={chip.vid} className="spr-number-price-usl-filter__chip">
            <button
              type="button"
              className="spr-number-price-usl-filter__chip-btn"
              title="Изменить значение"
              onClick={() => {
                cancelAdd();
                setEditVid(chip.vid);
              }}
            >
              <span
                className="spr-number-price-usl-filter__chip-kind"
                style={{ color: primKindUi(chip.vid).color }}
                title={chip.kindLabel}
              >
                <PrimKindIcon vid={chip.vid} />
              </span>
              <span className="spr-number-price-usl-filter__chip-value">{chip.valueLabel}</span>
            </button>
            <button
              type="button"
              className="spr-number-price-usl-filter__clear"
              title="Сбросить"
              aria-label={`Сбросить: ${chip.kindLabel}`}
              onClick={() => clearVid(chip.vid)}
            >
              <CloseOutlined />
            </button>
          </span>
        )
      )}
      {adding ? (
        <span className="spr-number-price-usl-filter__draft">
          <MiniSelect
            autoFocus={!draftVid}
            defaultOpen={!draftVid}
            options={availableKinds}
            placeholder="Справочник"
            value={draftVid}
            width={120}
            onChange={(vid) => setDraftVid(vid)}
          />
          <MiniSelect
            key={draftVid ?? "value"}
            autoFocus={draftVid != null}
            defaultOpen={draftVid != null}
            disabled={draftVid == null}
            options={draftVid != null ? optionsByVid?.[draftVid] || [] : []}
            placeholder="Значение"
            width={140}
            onChange={pickDraftValue}
          />
          <button
            type="button"
            className="spr-number-price-usl-filter__clear"
            title="Отменить"
            aria-label="Отменить добавление фильтра"
            onClick={cancelAdd}
          >
            <CloseOutlined />
          </button>
        </span>
      ) : null}
      {canAdd && !adding ? (
        <button
          type="button"
          className="spr-number-price-usl-filter__add"
          title="Добавить фильтр"
          aria-label="Добавить фильтр"
          onClick={startAdd}
        >
          <PlusOutlined />
        </button>
      ) : null}
    </div>
  );
};

export default SprNumberPriceUslFilter;
